import { useState } from 'react'
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Textarea } from '../../lib/shadcn/textarea'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { cn } from '../../lib/shadcn/utils'
import { Chip } from '../interviews/Chip'
import {
  useRemoveContentCaption,
  useSaveContentCaption,
  useSelectContentCaption,
} from '../../hooks/backend/marketing'
import { writeErrorMessage } from '../../utils/writeError'
import type { CaptionOption, CodeLabel, ContentRow } from '../../utils/marketing'

/**
 * The caption options, and choosing between them.
 *
 * THIS IS THE SCREEN, not a detail of it. The September plan supplies four
 * captions per post from two separate documents, and picking one is most of what
 * the approval actually is. A single caption box with the other three "somewhere"
 * is how eighty pieces of written work end up back in a WhatsApp thread.
 *
 * Selecting is one click on the option itself, not a dropdown, because the thing
 * being chosen is a paragraph and a dropdown cannot show a paragraph. The chosen
 * one is marked and the rest stay readable, since the reason to choose is the
 * comparison.
 */
export function CaptionOptions({
  item,
  ctas,
  canEdit,
  canChoose,
  reopensOnEdit = false,
  confirmReopen,
  onChanged,
}: {
  item: ContentRow
  ctas: CodeLabel[]
  canEdit: boolean
  canChoose: boolean
  // Approved content: every write here sends it back to Draft, so each one asks
  // first and tells the database it was confirmed (migration 0410).
  reopensOnEdit?: boolean
  confirmReopen?: () => Promise<boolean>
  onChanged: () => void
}) {
  const save = useSaveContentCaption()
  const select = useSelectContentCaption()
  const remove = useRemoveContentCaption()

  const [editing, setEditing] = useState<string | 'new' | null>(null)
  const [label, setLabel] = useState('')
  const [body, setBody] = useState('')
  const [cta, setCta] = useState('')
  const [tags, setTags] = useState('')
  const [busy, setBusy] = useState<string | null>(null)

  const options = item.caption_options ?? []

  function startEdit(o: CaptionOption) {
    setEditing(o.id)
    setLabel(o.label)
    setBody(o.body)
    setCta(o.cta ?? '')
    setTags((o.hashtags ?? []).join(' '))
  }

  function startNew() {
    setEditing('new')
    setLabel('')
    setBody('')
    // Inherits the post's CTA, because that is what the plan does: one CTA per
    // post, carried by every option. Overridable, because the long-form
    // September captions each state their own.
    setCta(item.cta ?? '')
    setTags((item.hashtags ?? []).join(' '))
  }

  const tagList = () => tags.split(/[\s,]+/).filter(Boolean)

  async function run(name: string, fn: (confirmed: boolean) => Promise<unknown>, ok: string) {
    let confirmed = false
    if (reopensOnEdit) {
      confirmed = (await confirmReopen?.()) ?? false
      if (!confirmed) return
    }
    setBusy(name)
    try {
      await fn(confirmed)
      toast.success(ok)
      setEditing(null)
      onChanged()
    } catch (err) {
      toast.error('That did not go through', { description: writeErrorMessage(err) })
    } finally {
      setBusy(null)
    }
  }

  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-xs font-semibold text-foreground">The caption</p>
        {options.length > 0 && (
          <Chip tone={item.selected_caption_id ? 'green' : 'amber'}>
            {item.selected_caption_id
              ? `${item.caption_label} chosen`
              : `${options.length} written, none chosen`}
          </Chip>
        )}
        {canEdit && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="ml-auto h-7 gap-1 px-2 text-xs"
            onClick={startNew}
          >
            <Plus className="h-3.5 w-3.5" />
            Add an option
          </Button>
        )}
      </div>

      {/* Named plainly. Four options and no decision is exactly what the writer
          is being asked to resolve, and the database refuses to submit in this
          state rather than sending the approver a choice they cannot make. */}
      {item.caption_not_chosen && (
        <p className="text-[11px] text-amber-700 dark:text-amber-300">
          Nothing is chosen yet. Pick the one that should go out; it cannot be sent for approval
          until you do.
        </p>
      )}

      {options.length === 0 && editing !== 'new' && (
        <p className="text-xs text-muted-foreground">
          No caption written yet.
          {canEdit ? ' Add an option to get started.' : ''}
        </p>
      )}

      <ul className="space-y-2">
        {options.map((o) => {
          const chosen = o.id === item.selected_caption_id
          if (editing === o.id) {
            return (
              <li key={o.id} className="space-y-2 rounded-md border border-border bg-muted/30 p-2.5">
                <div className="flex gap-2">
                  <Input
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder="Label"
                    className="h-8 w-32 text-xs"
                  />
                  <NativeSelect
                    value={cta}
                    onChange={(e) => setCta(e.target.value)}
                    className="h-8 flex-1 text-xs"
                  >
                    <option value="">No call to action</option>
                    {ctas.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.label}
                      </option>
                    ))}
                  </NativeSelect>
                </div>
                <Textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={6}
                  className="text-sm"
                />
                <Input
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="Hashtags, separated by spaces"
                  className="h-8 text-xs"
                />
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="brand"
                    className="h-7 gap-1 px-2 text-xs"
                    disabled={body.trim() === '' || busy !== null}
                    onClick={() =>
                      void run(
                        'save',
                        (confirm_reopen) =>
                          save.trigger({
                            id: o.id,
                            content_item_id: item.id,
                            label,
                            body,
                            cta: cta || null,
                            hashtags: tagList(),
                            confirm_reopen,
                          }).result,
                        'Caption saved',
                      )
                    }
                  >
                    {busy === 'save' ? <Spinner className="h-3 w-3" /> : <Check className="h-3 w-3" />}
                    Save
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-xs"
                    onClick={() => setEditing(null)}
                  >
                    Cancel
                  </Button>
                </div>
              </li>
            )
          }
          return (
            <li
              key={o.id}
              className={cn(
                'rounded-md border p-2.5',
                chosen
                  ? 'border-[hsl(var(--brand))]/60 bg-[hsl(var(--brand-soft))]/30'
                  : 'border-border bg-background',
              )}
            >
              <div className="flex flex-wrap items-center gap-1.5">
                <Chip tone={chosen ? 'brand' : 'neutral'}>{o.label}</Chip>
                {o.cta && <span className="text-[11px] text-muted-foreground">{o.cta}</span>}
                <span className="text-[11px] text-muted-foreground">
                  {o.body.length} characters
                </span>
                <div className="ml-auto flex items-center gap-1">
                  {canChoose && !chosen && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 text-xs"
                      disabled={busy !== null}
                      onClick={() =>
                        void run(
                          'select-' + o.id,
                          (confirm_reopen) =>
                            select.trigger({ content_item_id: item.id, option_id: o.id, confirm_reopen })
                              .result,
                          `${o.label} is the one that goes out`,
                        )
                      }
                    >
                      {busy === 'select-' + o.id ? (
                        <Spinner className="h-3 w-3" />
                      ) : (
                        'Use this one'
                      )}
                    </Button>
                  )}
                  {canEdit && (
                    <>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        aria-label={`Edit ${o.label}`}
                        className="h-7 w-7 p-0"
                        onClick={() => startEdit(o)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        aria-label={`Remove ${o.label}`}
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                        disabled={busy !== null}
                        onClick={() =>
                          void run(
                            'del-' + o.id,
                            (confirm_reopen) => remove.trigger({ id: o.id, confirm_reopen }).result,
                            'Option removed',
                          )
                        }
                      >
                        {busy === 'del-' + o.id ? (
                          <Spinner className="h-3 w-3" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </>
                  )}
                </div>
              </div>
              {/* Whole, not truncated. The entire reason there are four of these
                  is so a person can read them side by side and choose. */}
              <p className="mt-1.5 whitespace-pre-wrap text-sm text-foreground">{o.body}</p>
              {o.hashtags?.length > 0 && (
                <p className="mt-1 text-[11px] text-muted-foreground">{o.hashtags.join(' ')}</p>
              )}
            </li>
          )
        })}
      </ul>

      {editing === 'new' && (
        <div className="space-y-2 rounded-md border border-border bg-muted/30 p-2.5">
          <div className="flex gap-2">
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Label, or leave it"
              className="h-8 w-40 text-xs"
            />
            <NativeSelect
              value={cta}
              onChange={(e) => setCta(e.target.value)}
              className="h-8 flex-1 text-xs"
            >
              <option value="">No call to action</option>
              {ctas.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </NativeSelect>
          </div>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={6}
            placeholder="The caption as it will be posted"
            className="text-sm"
          />
          <Input
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="Hashtags, separated by spaces"
            className="h-8 text-xs"
          />
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant="brand"
              className="h-7 gap-1 px-2 text-xs"
              disabled={body.trim() === '' || busy !== null}
              onClick={() =>
                void run(
                  'save',
                  (confirm_reopen) =>
                    save.trigger({
                      id: null,
                      content_item_id: item.id,
                      label: label || null,
                      body,
                      cta: cta || null,
                      hashtags: tagList(),
                      confirm_reopen,
                    }).result,
                  'Caption added',
                )
              }
            >
              {busy === 'save' ? <Spinner className="h-3 w-3" /> : <Check className="h-3 w-3" />}
              Add
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 gap-1 px-2 text-xs"
              onClick={() => setEditing(null)}
            >
              <X className="h-3 w-3" />
              Cancel
            </Button>
          </div>
        </div>
      )}
    </section>
  )
}
